import { Button, Form } from 'antd';
import React from 'react';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import { TabContentWrapper } from '@/components/_common';
import { PageProps } from '@/types/PageProps';
import ProspectingForm from '@/components/CRM/Prospecting/ProspectingForm';
import {
  ReportCustomersProspectingForm,
  ReportCustomersProspectingParams,
} from '@/core/domain/reports/people/Report.types';
import { useReportGenerate } from '@/hooks';
import useFileLoading, { FileType } from '@/hooks/useFileLoading';
import { ReportCallback } from '@/hooks/useReportGenerate';
import { FormatType } from '@/core/domain/reports/movements/abcCurve/abcCurve.types';
import { SituationOptions } from '@/core/domain/reports/movements/workOrders/WorkOrderReport.types';
import { DateTimeFormat } from '@/utils/moment';
import useGenerateCustomersProspectingReport from '@/core/domain/reports/people/Report.hooks';

const ProspectingPage: React.FC<PageProps> = ({ route }) => {
  const intl = useIntl();
  const [form] = Form.useForm<ReportCustomersProspectingForm>();
  const { setFileLoading, setFileUnloading, fileLoading } = useFileLoading();

  const report = useReportGenerate();

  const generateCustomersProspectingReport =
    useGenerateCustomersProspectingReport();

  const handleGenerateReport = async (
    format: FileType,
    callback?: ReportCallback,
  ) => {
    setFileLoading(format);
    try {
      const values = await form.validateFields();

      const obj: ReportCustomersProspectingParams = {
        open: values?.situation.includes(SituationOptions.open),
        authorized: values?.situation.includes(SituationOptions.authorized),
        closed: values?.situation.includes(SituationOptions.closed),
        canceled: values?.situation.includes(SituationOptions.canceled),
        companiesIds: values?.companiesIds
          ? values?.companiesIds.toString()
          : '',
        customersIds: values?.customersIds
          ? values?.customersIds.toString()
          : '',
        dateFilterBy: values?.dateFilterBy,
        endDate: values?.endDate
          ? values?.endDate.format(DateTimeFormat)
          : undefined,
        startDate: values?.startDate
          ? values?.startDate.format(DateTimeFormat)
          : undefined,
        finalOilChangeKm: values?.finalOilChangeKm,
        finalOsKm: values?.finalOsKm,
        initialOilChangeKm: values?.initialOilChangeKm,
        initialOsKm: values?.initialOsKm,
        osType: values?.osType,
        serviceDescription: values?.serviceDescription,
        thirdParty: values?.thirdParty,
        showAllContacts: values?.showAllContacts,
        showOnlyMainContactsGroup: values?.showOnlyMainContactsGroup,
      };

      const result = await generateCustomersProspectingReport.mutateAsync(obj);

      if (callback) {
        callback(result);
      }
    } catch {}

    setFileUnloading(format);
  };

  const handleCsv = () => {
    handleGenerateReport(FormatType.CSV, (result) =>
      report.downloadFile({
        result,
        fileName: 'prospeccao.csv',
      }),
    );
  };

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <TabContentWrapper
        permissions={route.permissions}
        title={route.name}
        extra={
          <Button
            type="primary"
            block
            onClick={handleCsv}
            loading={fileLoading.CSV}
          >
            Gerar CSV
          </Button>
        }
      >
        <ProspectingForm form={form} />
      </TabContentWrapper>
    </>
  );
};

export default ProspectingPage;
